<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import DoughnutChart from 'shared/components/charts/DoughnutChart.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import {
  formatCurrencyValue,
  formatNumber,
  formatDimensionGroupName,
} from 'kanban/helper/reportFormatters';
import {
  DIMENSION_SLICE_COLORS,
  DIMENSION_COMPARE_COLORS,
  DIMENSION_MAX_SLICES,
} from 'kanban/constants/reports';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: 'en',
  },
  // O Painel geral fixa o eixo em produto e nao mostra o seletor de eixo; sem
  // titulo proprio o card virava "Participacao e comparacao" e ninguem achava os
  // produtos (E2E 12.3).
  title: {
    type: String,
    default: '',
  },
  infoText: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();

const measure = ref('revenue');

// No eixo de motivo de perda só existe card perdido, e a tabela já esconde ganho e receita.
// Oferecer essas medidas no gráfico mostraria o card que foi ganho e depois perdido, que é
// justamente o número que confunde quem lê.
const isLostReasonAxis = computed(
  () => props.data?.dimension?.type === 'lost_reason'
);

const measureOptions = computed(() => {
  const lost = {
    label: t('KANBAN.REPORTS.DIMENSION.CHART.BY_LOST'),
    value: 'lost',
  };
  if (isLostReasonAxis.value) return [lost];

  return [
    { label: t('KANBAN.REPORTS.DIMENSION.CHART.BY_REVENUE'), value: 'revenue' },
    { label: t('KANBAN.REPORTS.DIMENSION.CHART.BY_WON'), value: 'won' },
    lost,
  ];
});

// Lost cards carry no revenue, so the lost reason axis opens on the lost count; leaving it
// goes back to revenue instead of keeping a measure picked for that axis.
watch(
  () => props.data?.dimension?.type,
  (type, previousType) => {
    if (type === 'lost_reason') measure.value = 'lost';
    else if (previousType === 'lost_reason') measure.value = 'revenue';
  },
  { immediate: true }
);
const measureLabel = computed(
  () =>
    measureOptions.value.find(option => option.value === measure.value)?.label
);

const currency = computed(() => props.data?.currency || 'USD');
const rows = computed(() => props.data?.rows || []);

const rowName = row =>
  formatDimensionGroupName(row, props.data?.dimension, props.locale, t);
const valueOf = row => row[measure.value] || 0;
const previousValueOf = row => row[`${measure.value}_previous`] || 0;

const formatMeasure = value =>
  measure.value === 'revenue'
    ? formatCurrencyValue(value, currency.value, props.locale, {
        compact: true,
      })
    : formatNumber(value, props.locale);

// Slices are capped so the ring stays readable; whatever is left is one honest "Outros"
// slice instead of a dozen slivers.
const ranked = computed(() =>
  [...rows.value]
    .filter(row => valueOf(row) > 0)
    .sort((a, b) => valueOf(b) - valueOf(a))
);

const slices = computed(() => {
  const top = ranked.value.slice(0, DIMENSION_MAX_SLICES);
  const rest = ranked.value.slice(DIMENSION_MAX_SLICES);
  const result = top.map(row => ({ name: rowName(row), value: valueOf(row) }));
  if (rest.length) {
    result.push({
      name: t('KANBAN.REPORTS.DIMENSION.CHART.OTHERS', { count: rest.length }),
      value: rest.reduce((sum, row) => sum + valueOf(row), 0),
    });
  }
  return result;
});

const sliceTotal = computed(() =>
  slices.value.reduce((sum, slice) => sum + slice.value, 0)
);

const shareCollection = computed(() => ({
  labels: slices.value.map(slice => slice.name),
  datasets: [
    {
      data: slices.value.map(slice => slice.value),
      backgroundColor: slices.value.map(
        (_slice, index) =>
          DIMENSION_SLICE_COLORS[index % DIMENSION_SLICE_COLORS.length]
      ),
      borderWidth: 2,
      borderColor: 'transparent',
    },
  ],
}));

const shareOptions = computed(() => ({
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: context => `${context.label}: ${formatMeasure(context.parsed)}`,
      },
    },
  },
}));

// Percentages come from the same totals the legend prints, so the ring and the list can
// never disagree.
const legend = computed(() =>
  slices.value.map((slice, index) => ({
    name: slice.name,
    color: DIMENSION_SLICE_COLORS[index % DIMENSION_SLICE_COLORS.length],
    value: formatMeasure(slice.value),
    share: sliceTotal.value
      ? `${Math.round((slice.value / sliceTotal.value) * 100)}%`
      : '0%',
  }))
);

// Same values against the window of equal length immediately before the selected one.
const comparison = computed(() =>
  [...rows.value]
    .filter(row => valueOf(row) > 0 || previousValueOf(row) > 0)
    .sort((a, b) => valueOf(b) - valueOf(a))
    .slice(0, DIMENSION_MAX_SLICES)
);

// O gráfico de barras é o da biblioteca do fornecedor, que lê `categories` e `series`.
// Duas séries: o período escolhido e o imediatamente anterior.
const compareData = computed(() => ({
  categories: comparison.value.map(row => rowName(row)),
  series: [
    {
      id: 'current',
      label: t('KANBAN.REPORTS.DIMENSION.CHART.CURRENT'),
      color: DIMENSION_COMPARE_COLORS.current,
      data: comparison.value.map(row => valueOf(row)),
    },
    {
      id: 'previous',
      label: t('KANBAN.REPORTS.DIMENSION.CHART.PREVIOUS'),
      color: DIMENSION_COMPARE_COLORS.previous,
      data: comparison.value.map(row => previousValueOf(row)),
    },
  ],
}));

const isEmpty = computed(
  () => !slices.value.length && !comparison.value.length
);
</script>

<template>
  <KanbanReportWidget
    :title="title || t('KANBAN.REPORTS.DIMENSION.CHART.TITLE')"
    :info-text="infoText || t('KANBAN.REPORTS.DIMENSION.CHART.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="isEmpty"
    :empty-text="t('KANBAN.REPORTS.DIMENSION.EMPTY')"
    @retry="emit('retry')"
  >
    <template #actions>
      <SelectMenu
        :model-value="measure"
        :options="measureOptions"
        :label="measureLabel"
        icon="i-lucide-sigma"
        sub-menu-position="bottom"
        max-width="max-w-56"
        @update:model-value="measure = $event"
      />
    </template>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div class="flex flex-col gap-3">
        <h4 class="text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.REPORTS.DIMENSION.CHART.SHARE') }}
        </h4>
        <div class="flex flex-col items-center gap-4 sm:flex-row">
          <div class="h-48 w-48 flex-shrink-0">
            <DoughnutChart
              :collection="shareCollection"
              :chart-options="shareOptions"
            />
          </div>
          <ul class="flex flex-col gap-1.5 min-w-0 w-full">
            <li
              v-for="item in legend"
              :key="item.name"
              class="flex items-center gap-2 text-xs"
            >
              <span
                class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                :style="{ backgroundColor: item.color }"
              />
              <span class="truncate text-n-slate-12">{{ item.name }}</span>
              <span class="ms-auto tabular-nums text-n-slate-11 flex-shrink-0">
                {{ item.value }}
              </span>
              <span
                class="tabular-nums text-n-slate-10 w-9 text-right flex-shrink-0"
              >
                {{ item.share }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <h4 class="text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.REPORTS.DIMENSION.CHART.COMPARE') }}
        </h4>
        <div class="h-64">
          <BarChart
            :data="compareData"
            :aria-label="t('KANBAN.REPORTS.DIMENSION.CHART.COMPARE')"
            :height="256"
            :format-value="formatMeasure"
          />
        </div>
      </div>
    </div>
  </KanbanReportWidget>
</template>
